import type { TFunction } from 'i18next';
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePendingWrite } from '@/lib/use-pending-write';
import { problemCode, problemMessage } from '../api/problem';
import { SidePanel } from './SidePanel';

/** What a submit answers: done, a refusal that belongs to the name field, or one for the form. */
export type NameSubmitResult =
  { readonly ok: true } | { readonly ok: false; readonly field?: string; readonly form?: string };

/** A refused name: a name already taken belongs under the field, anything else under the form. */
export function nameRefusal(
  t: TFunction<'administration'>,
  error: unknown,
  status: number,
  takenCode: 'ORGANIZATION_NAME_TAKEN' | 'TENANT_NAME_TAKEN',
): NameSubmitResult {
  return problemCode(error) === takenCode
    ? { ok: false, field: problemMessage(t, error, status) }
    : { ok: false, form: problemMessage(t, error, status) };
}

/** The longest name the contract accepts (NameRequest, 200 after trimming). */
const MAX_NAME = 200;

function NameFooter({
  formId,
  pending,
  submitIcon,
  submitLabel,
  onCancel,
}: {
  readonly formId: string;
  readonly pending: boolean;
  readonly submitIcon: ReactNode;
  readonly submitLabel: string;
  readonly onCancel: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <>
      <Button variant="ghost" onClick={onCancel}>
        {t('namePanel.cancel')}
      </Button>
      <Button type="submit" form={formId} disabled={pending}>
        {submitIcon}
        {submitLabel}
      </Button>
    </>
  );
}

function refusalErrors(result: { readonly field?: string; readonly form?: string }) {
  return {
    ...(result.field !== undefined ? { field: result.field } : {}),
    ...(result.form !== undefined ? { form: result.form } : {}),
  };
}

function nameProblem(t: TFunction<'administration'>, trimmed: string): string | null {
  if (trimmed === '') return t('namePanel.required');
  return trimmed.length > MAX_NAME ? t('namePanel.tooLong', { max: MAX_NAME }) : null;
}

function NameField({
  name,
  onChange,
  error,
  help,
}: {
  readonly name: string;
  readonly onChange: (name: string) => void;
  readonly error: string | undefined;
  readonly help: string;
}) {
  const { t } = useTranslation('administration');
  const nameId = useId();
  const helpId = useId();
  const errorId = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={nameId} className="field-label">
        {t('namePanel.name')}
      </label>
      <Input
        id={nameId}
        value={name}
        onChange={(event) => onChange(event.target.value)}
        maxLength={MAX_NAME + 20}
        autoComplete="off"
        aria-invalid={error !== undefined}
        aria-describedby={error !== undefined ? `${errorId} ${helpId}` : helpId}
      />
      {error !== undefined ? (
        <p id={errorId} className="text-destructive text-sm">
          {error}
        </p>
      ) : null}
      <p id={helpId} className="text-muted-foreground text-[13px]">
        {help}
      </p>
    </div>
  );
}

/**
 * The area's one-field panel: a name, to create a tenant or an organization, or to rename one.
 * The code is generated by the server and never asked for (FR-AD-6). A name already taken is said
 * under the field; anything else under the form, in plain words.
 */
export function NamePanel({
  open,
  onOpenChange,
  title,
  description,
  initialName = '',
  help,
  note,
  submitLabel,
  submitIcon,
  onSubmit,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description?: string;
  readonly initialName?: string;
  readonly help: string;
  readonly note?: string;
  readonly submitLabel: string;
  readonly submitIcon?: ReactNode;
  readonly onSubmit: (name: string) => Promise<NameSubmitResult>;
}) {
  const { t } = useTranslation('administration');
  const { pending, run } = usePendingWrite();
  const [name, setName] = useState(initialName);
  const [errors, setErrors] = useState<{ field?: string; form?: string }>({});
  const formId = useId();

  // Each opening starts from the name it was opened with, without the last attempt's errors.
  useEffect(() => {
    if (open) {
      setName(initialName);
      setErrors({});
    }
  }, [open, initialName]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    const problem = nameProblem(t, trimmed);
    if (problem !== null) {
      setErrors({ field: problem });
      return;
    }
    void run(async () => {
      const result = await onSubmit(trimmed);
      if (result.ok) {
        onOpenChange(false);
        return;
      }
      setErrors(refusalErrors(result));
    });
  };

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      {...(description !== undefined ? { description } : {})}
      footer={
        <NameFooter
          formId={formId}
          pending={pending}
          submitIcon={submitIcon}
          submitLabel={submitLabel}
          onCancel={() => onOpenChange(false)}
        />
      }
    >
      <form id={formId} noValidate onSubmit={submit} className="flex flex-col gap-4">
        <NameField name={name} onChange={setName} error={errors.field} help={help} />
        {note !== undefined ? <p className="text-muted-foreground text-[13.5px]">{note}</p> : null}
        {errors.form !== undefined ? (
          <p role="alert" className="text-destructive text-sm">
            {errors.form}
          </p>
        ) : null}
      </form>
    </SidePanel>
  );
}
